import qs from 'qs';

import type { paths } from '@luxuryescapes/contract-svc-vendor';

import { json_headers, request } from './common';

type JsonContent<T> = T extends { content: { 'application/json': infer TContent } } ? TContent : never;
type OperationResponse<TOperation, TStatus extends number> = TOperation extends {
  responses: Record<TStatus, infer TResponse>;
}
  ? JsonContent<TResponse>
  : never;
type OperationRequestBody<TOperation> = TOperation extends {
  requestBody: { content: { 'application/json': infer TBody } };
}
  ? TBody
  : never;

type AdminListPendingChangesOperation = paths['/api/vendor/admin/extranet-pending-changes']['get'];
type AdminGetPendingChangeOperation = paths['/api/vendor/admin/extranet-pending-changes/{id}']['get'];
type AdminApprovePendingChangeOperation = paths['/api/vendor/admin/extranet-pending-changes/{id}/approve']['post'];
type AdminRejectPendingChangeOperation = paths['/api/vendor/admin/extranet-pending-changes/{id}/reject']['post'];
type AdminBulkApprovePendingChangesOperation = paths['/api/vendor/admin/extranet-pending-changes/bulk-approve']['post'];
type AdminBulkRejectPendingChangesOperation = paths['/api/vendor/admin/extranet-pending-changes/bulk-reject']['post'];

type ContractListExtranetPendingChangesParams = AdminListPendingChangesOperation['parameters']['query'];

export type ListExtranetPendingChangesParams = Omit<ContractListExtranetPendingChangesParams, 'page' | 'limit'> & {
  page?: number;
  limit?: number;
  propertyCategory?: string;
};

export type ListExtranetPendingChangesResponse = OperationResponse<AdminListPendingChangesOperation, 200>;
export type ExtranetPendingChange = ListExtranetPendingChangesResponse['result'][number];
export type ExtranetPendingChangeStatus = ExtranetPendingChange['status'];
export type ExtranetPendingChangeRequestType = ExtranetPendingChange['request_type'];
export type GetExtranetPendingChangeResponse = OperationResponse<AdminGetPendingChangeOperation, 200>;
export type ApproveExtranetPendingChangeResponse = OperationResponse<AdminApprovePendingChangeOperation, 200>;
export type RejectExtranetPendingChangeResponse = OperationResponse<AdminRejectPendingChangeOperation, 200>;
export type BulkApproveExtranetPendingChangesResponse = OperationResponse<AdminBulkApprovePendingChangesOperation, 200>;
export type BulkRejectExtranetPendingChangesResponse = OperationResponse<AdminBulkRejectPendingChangesOperation, 200>;
export type BulkActionResponse = BulkApproveExtranetPendingChangesResponse | BulkRejectExtranetPendingChangesResponse;
export interface EditExtranetPendingChangeResponse {
  status: 200;
  message: 'updated';
  result: ExtranetPendingChange;
}

export type EditExtranetPendingChangeValue = ExtranetPendingChange['proposed_value'];

type BulkActionRequestBody = OperationRequestBody<AdminBulkApprovePendingChangesOperation>;

function canonicalProposalJson(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map(canonicalProposalJson).join(',')}]`;
  }

  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return `{${Object.keys(record)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${canonicalProposalJson(record[key])}`)
      .join(',')}}`;
  }

  const serialized = JSON.stringify(value);
  if (serialized === undefined) {
    throw new Error('Pending change proposal is not JSON-serializable');
  }
  return serialized;
}

async function proposalRevision(value: Record<string, unknown>): Promise<string> {
  const serialized = canonicalProposalJson(value);

  const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(serialized));
  let hex = '';
  for (const byte of new Uint8Array(digest)) {
    hex += byte.toString(16).padStart(2, '0');
  }
  return hex;
}

export default class ExtranetService {
  static basePath() {
    return window.configs.API_HOST + '/api/vendor/admin/extranet-pending-changes';
  }

  static listPendingChanges(
    params: ListExtranetPendingChangesParams = {},
    options?: { signal?: AbortSignal },
  ): Promise<ListExtranetPendingChangesResponse> {
    const query = qs.stringify(params, { skipNulls: true });
    return request(`${this.basePath()}${query ? `?${query}` : ''}`, { signal: options?.signal });
  }

  static getPendingChangeById(
    id: string,
    options?: { signal?: AbortSignal },
  ): Promise<GetExtranetPendingChangeResponse> {
    return request(`${this.basePath()}/${id}`, { signal: options?.signal });
  }

  static async editChange(
    id: string,
    proposedValue: EditExtranetPendingChangeValue,
    expectedProposedValue: EditExtranetPendingChangeValue,
  ): Promise<EditExtranetPendingChangeResponse> {
    const body: Record<string, unknown> = {
      proposed_value: proposedValue,
      expected_revision: await proposalRevision(expectedProposedValue),
    };

    return request(`${this.basePath()}/${id}`, {
      method: 'PATCH',
      headers: { ...json_headers },
      body: JSON.stringify(body),
    });
  }

  static async approveChange(
    id: string,
    expectedProposedValue?: EditExtranetPendingChangeValue,
  ): Promise<ApproveExtranetPendingChangeResponse> {
    const body: Record<string, unknown> = {};
    if (expectedProposedValue !== undefined) {
      body.expected_revision = await proposalRevision(expectedProposedValue);
    }

    return request(`${this.basePath()}/${id}/approve`, {
      method: 'POST',
      headers: { ...json_headers },
      body: JSON.stringify(body),
    });
  }

  static rejectChange(id: string): Promise<RejectExtranetPendingChangeResponse> {
    return request(`${this.basePath()}/${id}/reject`, {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify({}),
    });
  }

  static bulkApprove(ids: BulkActionRequestBody['ids']): Promise<BulkApproveExtranetPendingChangesResponse> {
    const body: BulkActionRequestBody = { ids };
    return request(`${this.basePath()}/bulk-approve`, {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify(body),
    });
  }

  static bulkReject(ids: BulkActionRequestBody['ids']): Promise<BulkRejectExtranetPendingChangesResponse> {
    const body: BulkActionRequestBody = { ids };
    return request(`${this.basePath()}/bulk-reject`, {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify(body),
    });
  }
}
